import { useState } from "react";
import { CheckCheck, Loader2, AlertTriangle } from "lucide-react";
import { useExecuteAronWrite } from "../api/aronApi";
import type { AronPendingWrite } from "../types";

/** Minimum pending proposals before the bulk-accept bar appears. */
export const BATCH_ACCEPT_THRESHOLD = 10;

/** Proposals eligible for one-click bulk accept: still pending and a plain API
 * write. Scheduled-task proposals are excluded — their autonomous write_scope
 * warrants an explicit per-card decision, never a blanket approve. */
export function batchAcceptable(writes: AronPendingWrite[]): AronPendingWrite[] {
  return writes.filter((w) => w.status === "pending" && w.kind !== "schedule_task");
}

/**
 * "Zaakceptuj wszystko" — shown above the proposal list when ARON proposes many
 * writes at once. Executes each eligible proposal with its stored body (no edits),
 * sequentially through the same gated execute endpoint the individual cards use,
 * so the backend allow-list still validates every write as the user.
 */
export function AronBatchAccept({
  conversationId,
  writes,
}: {
  conversationId: string;
  writes: AronPendingWrite[];
}) {
  const execute = useExecuteAronWrite();
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState(0);
  const [total, setTotal] = useState(0);

  const targets = batchAcceptable(writes);
  // Keep the bar mounted through the whole run even as accepted writes drop out
  // of `targets` and the live count falls below the threshold.
  if (!running && targets.length < BATCH_ACCEPT_THRESHOLD) return null;

  const acceptAll = async () => {
    const snapshot = batchAcceptable(writes);
    if (!snapshot.length) return;
    setRunning(true);
    setTotal(snapshot.length);
    setDone(0);
    setFailed(0);
    for (const w of snapshot) {
      try {
        await execute.mutateAsync({ conversationId, writeId: w.id });
      } catch {
        setFailed((f) => f + 1);
      } finally {
        setDone((d) => d + 1);
      }
    }
    setRunning(false);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 border-l-[3px] border-l-red-600 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
        {running ? (
          <>
            <Loader2 size={14} className="animate-spin text-red-600" />
            <span>
              Zatwierdzanie… {done} z {total}
              {failed > 0 && (
                <span className="ml-1 inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle size={12} /> {failed} nieudanych
                </span>
              )}
            </span>
          </>
        ) : (
          <span>
            ARON proponuje <span className="font-medium">{targets.length}</span> zapisów.
          </span>
        )}
      </div>
      <button
        type="button"
        disabled={running || targets.length === 0}
        onClick={acceptAll}
        className="inline-flex items-center gap-1.5 bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-40"
      >
        {running ? (
          <Loader2 size={13} className="animate-spin" />
        ) : (
          <CheckCheck size={13} />
        )}
        Zaakceptuj wszystko ({targets.length})
      </button>
    </div>
  );
}
